import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { generateLessonContentPDF } from '../../utils/pdfGenerator';
import { 
    ArrowLeftIcon, 
    ArrowDownTrayIcon,
    BookOpenIcon,
    ClockIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const StudentNoteView = () => {
    const { id: courseId, noteId } = useParams();
    const navigate = useNavigate();
    const { get } = useApi();

    const { data: course, isLoading: courseLoading } = useQuery({
        queryKey: ['course', courseId],
        queryFn: () => get(`/courses/api/${courseId}/`)
    });

    // Find the specific note from the course data
    const note = React.useMemo(() => {
        if (!course || !course.modules) return null;
        for (const module of course.modules) {
            for (const lesson of module.lessons) {
                const found = lesson.contents?.find(c => c.id.toString() === noteId);
                if (found) return { ...found, lessonTitle: lesson.title };
            }
        }
        return null;
    }, [course, noteId]);

    if (courseLoading) return <LoadingSpinner fullScreen />;

    if (!note) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mb-6">
                    <BookOpenIcon className="w-10 h-10 text-gray-400" />
                </div>
                <h1 className="text-2xl font-black text-[#18216D] mb-2">Note Not Found</h1>
                <p className="text-gray-500 mb-8 max-w-md text-center">We couldn't find the study material you're looking for. It may have been removed or moved.</p>
                <button 
                    onClick={() => navigate(`/courses/api/${courseId}/`)}
                    className="flex items-center gap-2 px-6 py-3 bg-[#18216D] text-white font-bold rounded-2xl hover:bg-[#253299] transition-all shadow-lg shadow-indigo-900/20"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Learning Journey
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Top Navigation Bar */}
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
                    <button 
                        onClick={() => navigate(`/courses/api/${courseId}/`)}
                        className="flex items-center gap-2 text-gray-500 hover:text-[#18216D] font-bold text-sm transition-colors group"
                    >
                        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-indigo-50 transition-colors">
                            <ArrowLeftIcon className="w-5 h-5" />
                        </div>
                        <span className="hidden sm:inline">Back to Journey</span>
                    </button>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => generateLessonContentPDF(note, note.lessonTitle)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-50 text-[#18216D] font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-indigo-100 transition-all border border-indigo-100"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">Save as PDF</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Reading Content Area */}
            <main className="max-w-3xl mx-auto px-6 py-12 pb-32">
                <header className="mb-12">
                    <div className="flex items-center gap-2 text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4">
                        <span className="bg-indigo-50 px-2 py-1 rounded">{note.lessonTitle}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-gray-400">Study Materials</span>
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl font-black text-[#18216D] tracking-tight leading-tight mb-6">
                        {note.title === 'Text Content' ? 'Detailed Lesson Notes' : note.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-6 text-sm font-medium text-gray-400">
                        <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4" />
                            <span>About {Math.max(1, Math.ceil(note.body.length / 1500))} min read</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4" />
                            <span>Updated recently</span>
                        </div>
                    </div>
                </header>

                <div className="border-t border-gray-100 pt-12">
                    <div 
                        className="prose prose-slate prose-lg max-w-none 
                            prose-headings:text-[#18216D] prose-headings:font-black prose-headings:tracking-tight
                            prose-p:text-gray-600 prose-p:leading-relaxed prose-p:font-medium
                            prose-strong:text-[#18216D] prose-strong:font-black
                            prose-li:text-gray-600 prose-li:font-medium
                            prose-img:rounded-3xl prose-img:shadow-xl
                            prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 prose-blockquote:bg-indigo-50/30 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl prose-blockquote:font-medium prose-blockquote:italic prose-blockquote:text-indigo-900"
                        dangerouslySetInnerHTML={{ __html: note.body }}
                    />
                </div>

                <div className="mt-20 pt-12 border-t border-gray-100 flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-[#18216D]/5 rounded-2xl flex items-center justify-center mb-6">
                        <BookOpenIcon className="w-8 h-8 text-[#18216D]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#18216D] mb-2">End of Notes</h3>
                    <p className="text-gray-500 mb-8">You've reached the end of this study material. Ready for the next sub-strand?</p>
                    <button 
                        onClick={() => navigate(`/courses/api/${courseId}/`)}
                        className="px-10 py-4 bg-[#18216D] text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-[#253299] transition-all shadow-xl shadow-indigo-900/20"
                    >
                        Back to Course Overview
                    </button>
                </div>
            </main>
        </div>
    );
};

export default StudentNoteView;
